import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep, get, set } from 'lodash';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import type { MaasGroupSpec, MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasRunMode as RunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import type { ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { VOLUME_SOURCE_EPHEMERAL, VOLUME_SOURCE_PVC_TEMPLATE } from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { getWorkerGroupListForSubmit } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { resolveImageDeployVolumePathsFromItem } from '@/delivery-type-plugin/capabilities/maas-system-config/containerDeployConfigCore';

export const IMAGE_STORAGE_VOLUMES_FIELD = '_imageStorageVolumes';

export type ImageStorageVolumeKind = 'persistent' | 'ephemeral';

/** 镜像 containerDeployConfig 解析出的存储卷（仅 hidden 字段，不在 UI 展示） */
export interface ImageStorageVolumeItem {
  mountPath: string;
  kind: ImageStorageVolumeKind;
  volume: Record<string, unknown>;
}

export function resolveImageDeployVolumePaths(imageItem?: CommonImageItemType | null): {
  persistent: string[];
  ephemeral: string[];
} {
  return resolveImageDeployVolumePathsFromItem(imageItem);
}

export function buildImageEphemeralVolume(mountPath: string): Record<string, unknown> {
  return {
    type: 'Data',
    reclaimPolicy: 'Delete',
    source: {
      type: VOLUME_SOURCE_EPHEMERAL,
      mountPath,
    },
  };
}

export function buildImagePersistentVolume(mountPath: string): Record<string, unknown> {
  return {
    type: 'Data',
    reclaimPolicy: 'Delete',
    source: {
      type: VOLUME_SOURCE_PVC_TEMPLATE,
      mountPath,
    },
  };
}

/** 从选中镜像重建临时卷与持久卷（顺序：临时卷在前，持久卷在后） */
export function rebuildImageStorageVolumes(imageItem?: CommonImageItemType | null): ImageStorageVolumeItem[] {
  const { persistent, ephemeral } = resolveImageDeployVolumePaths(imageItem);

  return [
    ...ephemeral.map(
      (mountPath): ImageStorageVolumeItem => ({
        mountPath,
        kind: 'ephemeral',
        volume: buildImageEphemeralVolume(mountPath),
      }),
    ),
    ...persistent.map(
      (mountPath): ImageStorageVolumeItem => ({
        mountPath,
        kind: 'persistent',
        volume: buildImagePersistentVolume(mountPath),
      }),
    ),
  ];
}

export function serializeImageStorageVolumesField(items: ImageStorageVolumeItem[]): string | undefined {
  return items.length ? JSON.stringify(items) : undefined;
}

export function parseImageStorageVolumesField(raw: unknown): ImageStorageVolumeItem[] {
  if (raw == null || raw === '') return [];
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const mountPath = String(row.mountPath ?? '').trim();
      const kind = row.kind === 'persistent' || row.kind === 'ephemeral' ? row.kind : undefined;
      const volume = row.volume;
      const volumeObj = volume && typeof volume === 'object' && !Array.isArray(volume) ? (volume as Record<string, unknown>) : undefined;
      if (!mountPath || !kind || !volumeObj) return null;
      return { mountPath, kind, volume: cloneDeep(volumeObj) } as ImageStorageVolumeItem;
    })
    .filter(Boolean) as ImageStorageVolumeItem[];
}

export function parseImageStorageVolumesFromValues(values: ModelInstanceFormValues): ImageStorageVolumeItem[] {
  return parseImageStorageVolumesField(values[IMAGE_STORAGE_VOLUMES_FIELD as keyof ModelInstanceFormValues]);
}

export function writeImageStorageVolumesField(form: FormInstance, items: ImageStorageVolumeItem[]) {
  form.setFieldValue(IMAGE_STORAGE_VOLUMES_FIELD, serializeImageStorageVolumesField(items));
}

export function clearImageStorageVolumesFromForm(form: FormInstance) {
  writeImageStorageVolumesField(form, []);
}

/** 选中/切换镜像后更新 hidden 存储卷字段 */
export function applyImageStorageVolumesToForm(form: FormInstance, imageItem?: CommonImageItemType | null) {
  writeImageStorageVolumesField(form, rebuildImageStorageVolumes(imageItem));
}

export function resolveImageStorageVolumeNames(items: ImageStorageVolumeItem[]): string[] {
  return items.map((_, index) => `data${index}`);
}

/** 确认页：镜像存储卷描述项 */
export function buildImageStorageVolumeSummaryItems(
  item: ImageStorageVolumeItem,
  t: (key: string) => string,
): { label: string; value: string }[] {
  return [
    {
      label: t('data-center:maas.model.mountPath'),
      value: String(item.mountPath || '').trim() || '-',
    },
    {
      label: t('data-center:shared.volume'),
      value: item.kind === 'ephemeral' ? t('common:ephemeral.volume') : '持久卷',
    },
  ];
}

/** 提交用 Data 卷：镜像卷命名为 data0、data1 … */
export function buildImageStorageVolumesForSubmit(
  items: ImageStorageVolumeItem[],
  options?: { excludeMountPaths?: string[] },
): Record<string, unknown>[] {
  const excludeSet = new Set((options?.excludeMountPaths || []).map((path) => path.trim()).filter(Boolean));

  return items
    .map((item, index) => {
      if (item.kind === 'persistent' && excludeSet.has(item.mountPath.trim())) {
        return undefined;
      }
      const volume = cloneDeep(item.volume);
      volume.name = `data${index}`;
      return volume;
    })
    .filter(Boolean) as Record<string, unknown>[];
}

/** 将镜像存储卷追加到已组装的组 spec（模型 Data 卷之后，供 finalizeGroupSpecForSubmit 保留在 rest） */
export function mergeImageStorageVolumesIntoSubmitValues<T extends MaasSharedFormValues>(
  submitValues: T,
  sourceValues: ModelInstanceFormValues,
): T {
  const imageItems = parseImageStorageVolumesFromValues(sourceValues);
  if (!imageItems.length) return submitValues;

  const modelMountPath = String(sourceValues.model?.mountPath ?? '').trim();
  const imageVolumes = buildImageStorageVolumesForSubmit(imageItems, {
    excludeMountPaths: modelMountPath ? [modelMountPath] : undefined,
  });
  if (!imageVolumes.length) return submitValues;

  const next = cloneDeep(submitValues);

  const mergeIntoGroup = (group: MaasGroupSpec | undefined): MaasGroupSpec | undefined => {
    if (!group) return group;
    const volumes = (get(group, 'resource.storage.volume') as Record<string, unknown>[] | undefined) || [];
    set(group, 'resource.storage.volume', [...volumes, ...imageVolumes.map((volume) => cloneDeep(volume))]);
    return group;
  };

  const runMode = next.runMode;

  if (runMode === RunMode.PrimarySecondary && next.headGroupSpec) {
    next.headGroupSpec = mergeIntoGroup(next.headGroupSpec);
  }

  const workers = getWorkerGroupListForSubmit(next);
  if (workers.length > 0) {
    next.workerGroupSpecs = workers.map((worker) => mergeIntoGroup(worker)!);
  }

  return next;
}
